<script>
import { DraftTypes } from '@/domain/drafts/draft';
import { useHarbourStore } from '@/stores/harbour-store';
import { useDraftsStore } from '@/pages/Drafts/stores/drafts-store';
import { useSuperdocStore } from '@/stores/superdoc-store';
import Vue from "vue";

export default {
  name: 'DraftsViewersRenderer',

  setup() {
    const harbourStore = useHarbourStore();
    const draftsStore = useDraftsStore();
    const superdocStore = useSuperdocStore();
    return {
      harbourStore,
      draftsStore,
      superdocStore,
    };
  },

  data() {
    return {};
  },

  computed: {
    draft() {
      return this.params.data;
    },
    isPdfType() {
      return this.draft.type === DraftTypes.Pdf;
    },
    isUserOwner() {
      return !this.draft.isShared;
    },
    reader() {
      return this.draft.reader.filter(email => !this.draft.collaboratorsWithInheritedRoles.includes(email));
    },
    commentator() {
      return this.draft.commentator.filter(email => !this.draft.collaboratorsWithInheritedRoles.includes(email));
    },
    viewers() {
      return [...this.reader, ...this.commentator];
    },
    isSuperdoc() {
      return this.params.data.type === DraftTypes.SuperDoc;
    },
    hasViewers() {
      return this.viewers.length > 0;
    },
    isMore5Viewers() {
      return this.viewers.length > 5;
    },
    viewersToRender() {
      if (this.isMore5Viewers) return this.viewers.slice(0, 4);
      return this.viewers;
    },
    otherViewers() {
      return this.viewers.slice(4);
    },
    othersCount() {
      const others = this.otherViewers;
      return `+${others.length}`;
    },
    othersTooltipLabel() {
      const details = this.otherViewers.map((viewer) => {
        return `<div>${this.getTooltipLabel(viewer)}</div>`;
      });
      return details.join('');
    },
    canShare() {
      if (this.isPdfType) return false;
      const systemEmail = this.harbourStore.contextDict.systememail;
      const isWriter = this.draft.writer.includes(systemEmail);
      const isOrgAdmin = this.draftsStore.checkUserOrgAdminPermission(this.draft.orgId);
      return this.isUserOwner || isWriter || isOrgAdmin;
    },
  },

  methods: {
    getProfileImageUrl(email) {
      const profile = this.harbourStore.profilePictures.find((i) => i.email === email);
      return profile?.profileImageUrl;
    },

    getInitial(email) {
      const name = this.getNameByEmail(email);
      if (name) return name[0].toUpperCase();
      else if (email) return email[0].toUpperCase();
      return '*';
    },

    getTooltipLabel(email) {
      const name = this.getNameByEmail(email);
      const isCommentator = this.commentator.includes(email);
      const role = isCommentator ? 'Commentator' : 'Reader';
      if (!name) return `(${email}) - ${role}`;
      return `${name} (${email}) - ${role}`;
    },

    getNameByEmail(email) {
      return this.draftsStore.emailsToNames[email] || null;
    },

    onAddButtonClick() {
      if (!this.canShare) return;
      if (this.isSuperdoc) return this.superdocStore.openSuperdocShareModal(this.draft, this);

      this.openCkShareModal({
        ckFileId: this.draft.ckFileId,
        name: this.draft.name,
      });
    },

    openCkShareModal({ ckFileId, name }) {
      this.ckUpgradeModal( ckFileId, name )
    },

    ckUpgradeModal(ckFileId, agreementName){
      const hostname = window.location.hostname;
      const upgradeProps = {
        docId: ckFileId,
        docName: agreementName,
        parent: instance.proxy,
        orgName: hostname
      }
      Vue.prototype.$openCkeditorUpgradeModal({props: upgradeProps});
    }
  },
};
</script>

<template>
  <div class="draft-viewers-column">
    <div class="draft-viewers-column__container">
      <template v-if="hasViewers">
        <div
          v-for="(viewer, index) in viewersToRender"
          :key="index"
          class="draft-viewers-column__viewer"
          :class="[`draft-viewers-column__viewer--${index + 1}`]">
          <b-tooltip
            :label="getTooltipLabel(viewer)"
            :append-to-body="true"
            type="is-dark"
            class="draft-viewers-column__tooltip">
            <div class="draft-viewers-column__image">
              <img
                v-if="getProfileImageUrl(viewer)"
                :src="getProfileImageUrl(viewer)"
                class="draft-viewers-column__img" />
              <div v-else class="draft-viewers-column__initials">
                {{ getInitial(viewer) }}
              </div>
            </div>
          </b-tooltip>
        </div>

        <div class="draft-viewers-column__others" v-if="isMore5Viewers">
          <b-tooltip
            :append-to-body="true"
            :multilined="true"
            type="is-dark"
            class="draft-viewers-column__others-tooltip">
            <div class="draft-viewers-column__others-count">
              <div class="draft-viewers-column__others-count">{{ othersCount }}</div>
            </div>
            <template v-slot:content>
              <div
                class="draft-viewers-column__others-tooltip-content"
                v-html="othersTooltipLabel"></div>
            </template>
          </b-tooltip>
        </div>
      </template>

      <div
        class="draft-viewers-column__add-button"
        :class="{'draft-viewers-column__add-button--disabled': !canShare}"
        @click="onAddButtonClick">
        <i class="fa-solid fa-plus"></i>
      </div>

    </div>
  </div>
</template>

<style lang="postcss" scoped>
.draft-viewers-column {
  padding: 0 5px;

  &__container {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    width: 100%;
  }

  &__viewer {
    display: inline-flex;
    position: relative;

    &--1 {
      z-index: 0;
    }
    &--2 {
      z-index: 1;
      margin-left: -6px;
    }
    &--3 {
      z-index: 2;
      margin-left: -6px;
    }
    &--4 {
      z-index: 3;
      margin-left: -6px;
    }
    &--5 {
      z-index: 4;
      margin-left: -6px;
    }

    :deep(.b-tooltip .tooltip-trigger) {
      display: inline-flex;
    }
  }

  &__add-button {
    --size: 28px;
    font-size: 16px;
    color: #333;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--size);
    height: var(--size);
    border-radius: 50%;
    background-color: #fff;
    border: 1px solid #DBDBDB;
    flex-shrink: 0;
    overflow: hidden;
    cursor: pointer;
    transition: all .2s;

    &:hover {
      background-color: #DBDBDB;
    }

    &--disabled {
      color: #ccc;
      cursor: not-allowed;
      pointer-events: none;
    }
  }

  &__image {
    --size: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--size);
    height: var(--size);
    border-radius: 50%;
    background-color: #849cb8;
    border: 1px solid #DBDBDB;
    flex-shrink: 0;
    overflow: hidden;
  }

  &__img {
    width: var(--size);
    height: var(--size);
    border-radius: 50%;
    object-fit: cover;
    overflow: hidden;
    pointer-events: none;
    user-select: none;
  }

  &__initials {
    font-size: 14px;
    color: #fff;
    line-height: 1;
  }

  &__others {
    display: inline-flex;
    position: relative;
    z-index: 5;
    margin-left: -6px;

    :deep(.b-tooltip .tooltip-trigger) {
      display: inline-flex;
    }
  }

  &__others-count {
    --size: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--size);
    height: var(--size);
    border-radius: 50%;
    background-color: #d1dbf4;
    border: 1px solid #DBDBDB;
    flex-shrink: 0;
    overflow: hidden;
  }

  &__others-count-text {
    font-size: 12px;
    color: #333;
    line-height: 1;
  }
}
</style>

<style lang="postcss">
.draft-viewers-column__tooltip .tooltip-content,
.draft-viewers-column__others-tooltip .tooltip-content {
  font-size: 12px;
  color: #fff;
}

.draft-viewers-column__others-tooltip-content {
  text-align: left;
  white-space: nowrap;
  line-height: 1.4;

  & > div {
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.draft-viewers-column__others-tooltip .tooltip-content {
  width: 300px!important;
}
</style>
